import { create } from 'zustand';

/**
 * Tenant-level Twilio softphone feature flag, kept in a tiny dedicated
 * store so settings-menu `visible()` callbacks (which run synchronously
 * and outside of React) and page-level guards (inside React) can both
 * read the same value without each one re-fetching the runtime status.
 *
 * Populated once at app boot by `TwilioSoftphoneIntegration` after it
 * resolves `getRuntimeStatus()`. Stays put for the rest of the session;
 * a hard refresh is required to pick up a tenant-side flag flip. That's
 * acceptable given this is a once-per-tenant onboarding/cutover toggle,
 * not a daily knob.
 *
 * `null` = not yet loaded (auth hasn't resolved or the runtime-status
 * call is still in flight). Treat null as "off-for-now" so the gates
 * never accidentally surface softphone UI before we know the answer.
 */
interface SoftphoneFeatureFlagState {
	twilioFeatureEnabled: boolean | null;
	setTwilioFeatureEnabled: (enabled: boolean | null) => void;
}

export const useSoftphoneFeatureFlagStore = create<SoftphoneFeatureFlagState>(
	(set) => ({
		twilioFeatureEnabled: null,
		setTwilioFeatureEnabled: (enabled) =>
			set({ twilioFeatureEnabled: enabled }),
	}),
);

/**
 * Synchronous read for callers that can't subscribe to the store
 * (settings-menu `visible()` is one — it runs during nav-render and
 * isn't a React hook context).
 */
export function getTwilioFeatureEnabled(): boolean | null {
	return useSoftphoneFeatureFlagStore.getState().twilioFeatureEnabled;
}
